import React, { useEffect, useMemo, useState } from "react"

import { MoreVertical } from "lucide-react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useSendAuthLink } from "@/hooks/useSendAuthLink"
import { useSendResetPassword } from "@/hooks/useSendResetPassword"
import { useUserActivation } from "@/hooks/useUserActivation"
import { User, UsersFilters, useUsers } from "@/hooks/useUsers"
import { supabase } from "@/lib/supabase"

import { CreateUserDialog } from "./CreateUserDialog"
import { EditUserDialog } from "./EditUserDialog"

const UserManagementContent: React.FC = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [subTeamOptions, setSubTeamOptions] = useState<{ value: string; label: string }[]>([])

  // Top-level org: HIVED vs DSP, and sub-level: Staff vs Drivers
  const [org, setOrg] = useState<"hived" | "dsp">("hived")
  const [kind, setKind] = useState<"staff" | "drivers">("staff")

  // Pagination and filter/search state
  const PAGE_SIZE = 100
  const [page, setPage] = useState<number>(1)
  const [search, setSearch] = useState<string>("")
  const [debouncedSearch, setDebouncedSearch] = useState<string>("")
  const [roleFilter, setRoleFilter] = useState<string>("any") // any|Admin|Manager|Driver|DSP
  const [teamFilter, setTeamFilter] = useState<string>("")
  const [subTeamsFilter, setSubTeamsFilter] = useState<string>("") // comma-separated
  const [statusFilter, setStatusFilter] = useState<string>("any") // any|active|inactive

  const baseFilters = useMemo<UsersFilters>(() => {
    // Derive server-side filters from UI selection
    if (org === "hived" && kind === "staff") {
      return { roles: ["Admin", "Manager"], dsp: "any" }
    }
    if (org === "hived" && kind === "drivers") {
      return { roles: ["Driver"], dsp: "without" }
    }
    if (org === "dsp" && kind === "staff") {
      return { roles: ["DSP"], dsp: "any" }
    }
    // org === "dsp" && kind === "drivers"
    return { roles: ["Driver"], dsp: "with" }
  }, [org, kind])

  // Merge base filters with user-selected filters
  const effectiveFilters: UsersFilters = useMemo(() => {
    const roles = roleFilter !== "any" ? [roleFilter] : baseFilters.roles
    const isActive =
      statusFilter === "active" ? true : statusFilter === "inactive" ? false : undefined
    const subTeams = subTeamsFilter
      ? subTeamsFilter
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : undefined
    return {
      ...baseFilters,
      roles,
      team: teamFilter || null,
      q: debouncedSearch || undefined,
      isActive,
      subTeams,
      limit: PAGE_SIZE,
      page,
    }
  }, [baseFilters, roleFilter, teamFilter, debouncedSearch, statusFilter, subTeamsFilter, page])

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 400)
    return () => clearTimeout(id)
  }, [search])

  useEffect(() => {
    const fetchOptions = async () => {
      // Fetch Driver Manager Teams
      const { data: teamsData } = await supabase
        .from("driver_manager_teams")
        .select("name")
        .order("name")

      const teamOptions =
        teamsData?.map((t) => ({
          value: t.name,
          label: `${t.name}`,
        })) ?? []

      // Fetch DSPs
      const { data: dspData } = await supabase.from("dsps").select("dsp").order("dsp")

      const dspOptions =
        dspData?.map((d) => ({
          value: d.dsp,
          label: `${d.dsp}`,
        })) ?? []

      setSubTeamOptions([...teamOptions, ...dspOptions])
    }

    fetchOptions()
  }, [])

  // Fetch users with server-side filters via TanStack Query
  const { data, isLoading, isError, error } = useUsers(effectiveFilters)
  const users = data?.users ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // Reset to first page when base filters or search change significantly
  useEffect(() => {
    setPage(1)
  }, [org, kind, roleFilter, teamFilter, subTeamsFilter, statusFilter, debouncedSearch])

  const clearFilters = () => {
    setSearch("")
    setDebouncedSearch("")
    setRoleFilter("any")
    setTeamFilter("")
    setSubTeamsFilter("")
    setStatusFilter("any")
    setPage(1)
  }

  const isStaff = kind === "staff"
  const isDSPDrivers = org === "dsp" && kind === "drivers"
  const columnCount = isStaff ? 7 : isDSPDrivers ? 6 : 5

  // User activation mutations
  const { activateUser, deactivateUser } = useUserActivation()
  const sendAuthLink = useSendAuthLink()
  const sendResetPassword = useSendResetPassword()
  const handleEditUser = (user: User) => {
    setSelectedUser(user)
    setIsEditDialogOpen(true)
  }

  const handleToggleActivation = (user: User) => {
    if (user.isActive) {
      deactivateUser.mutate(
        { userId: user.id },
        {
          onSuccess: () => {
            toast.success(`${user.fullName} has been deactivated`)
          },
          onError: (error) => {
            toast.error(
              `Failed to deactivate user: ${
                error instanceof Error ? error.message : "Unknown error"
              }`,
            )
          },
        },
      )
    } else {
      activateUser.mutate(
        { userId: user.id },
        {
          onSuccess: () => {
            toast.success(`${user.fullName} has been activated`)
          },
          onError: (error) => {
            toast.error(
              `Failed to activate user: ${
                error instanceof Error ? error.message : "Unknown error"
              }`,
            )
          },
        },
      )
    }
  }

  const handleResendInvite = (user: User) => {
    if (!user.email) {
      toast.error("User email is missing")
      return
    }

    sendAuthLink.mutate(
      { email: user.email, linkType: "invite" },
      {
        onSuccess: (data) => {
          toast.success(data?.message || `Invite link resent to ${user.email}`)
        },
        onError: (err) => {
          toast.error(
            `Failed to resend invite: ${err instanceof Error ? err.message : "Unknown error"}`,
          )
        },
      },
    )
  }

  const handleSendResetPassword = (user: User) => {
    if (!user.email) {
      toast.error("User email is missing")
      return
    }

    sendResetPassword.mutate(
      { email: user.email },
      {
        onSuccess: (data) => {
          toast.success(data?.message || `Reset password link sent to ${user.email}`)
        },
        onError: (err) => {
          toast.error(
            `Failed to send reset password link: ${
              err instanceof Error ? err.message : "Unknown error"
            }`,
          )
        },
      },
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-secondary">User Management</h1>
      </div>

      {/* Row 1: Tabs (left) and Invite button (right) */}
      <div className="mb-2 flex items-center justify-between">
        <Tabs value={org} onValueChange={(v) => setOrg(v as "hived" | "dsp")}>
          <TabsList>
            <TabsTrigger value="hived">HIVED</TabsTrigger>
            <TabsTrigger value="dsp">DSP</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={kind === "drivers"}
          title={kind === "drivers" ? "Drivers cannot be created via the UI" : undefined}
        >
          {isStaff ? "Invite Staff" : "Invite User"}
        </Button>
      </div>

      {/* Row 2: All filters */}
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-[160px]">
            <Select value={kind} onValueChange={(v) => setKind(v as "staff" | "drivers")}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="drivers">Drivers</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-[200px]">
            <Input
              placeholder="Search name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-[160px]">
            <Select value={roleFilter} onValueChange={(v) => setRoleFilter(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any role</SelectItem>
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Manager">Manager</SelectItem>
                <SelectItem value="DSP">DSP</SelectItem>
                <SelectItem value="Driver">Driver</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-[220px]">
            <Select
              value={teamFilter || "any"}
              onValueChange={(v) => setTeamFilter(v === "any" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any team</SelectItem>
                <SelectItem value="Central Operations">Central Operations</SelectItem>
                <SelectItem value="Driver Operations">Driver Operations</SelectItem>
                <SelectItem value="Customer Service">Customer Service</SelectItem>
                <SelectItem value="Fleet">Fleet</SelectItem>
                <SelectItem value="Depot">Depot</SelectItem>
                <SelectItem value="Revenue Protection">Revenue Protection</SelectItem>
                <SelectItem value="Finance">Finance</SelectItem>
                <SelectItem value="Admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-[220px]">
            <Select
              value={subTeamsFilter || "any"}
              onValueChange={(v) => setSubTeamsFilter(v === "any" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Sub Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any</SelectItem>
                {subTeamOptions.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-[160px]">
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Button variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {isStaff ? (
                <>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  {org === "hived" ? <TableHead>Team</TableHead> : <TableHead>DSP ID</TableHead>}
                  <TableHead>Sub Teams</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[150px]">Actions</TableHead>
                </>
              ) : isDSPDrivers ? (
                <>
                  <TableHead>Full Name</TableHead>
                  <TableHead>Person</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Areas</TableHead>
                  <TableHead>DSP ID</TableHead>
                  <TableHead className="w-[150px]">Actions</TableHead>
                </>
              ) : (
                <>
                  <TableHead>Full Name</TableHead>
                  <TableHead>Person</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Areas</TableHead>
                  <TableHead className="w-[150px]">Actions</TableHead>
                </>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading state (single row spanning all columns)
              <TableRow>
                <TableCell colSpan={columnCount}>
                  <Skeleton className="h-6 w-full" />
                </TableCell>
              </TableRow>
            ) : isError ? (
              // Error state
              <TableRow>
                <TableCell colSpan={columnCount} className="text-center py-4 text-red-500">
                  Error loading users: {error instanceof Error ? error.message : "Unknown error"}
                </TableCell>
              </TableRow>
            ) : users && users.length > 0 ? (
              // Data loaded successfully
              users.map((user) => (
                <TableRow key={user.id}>
                  {isStaff ? (
                    <>
                      <TableCell>{user.fullName || "N/A"}</TableCell>
                      <TableCell>{user.email || "N/A"}</TableCell>
                      <TableCell>{user.role || "N/A"}</TableCell>
                      {org === "hived" ? (
                        <TableCell>{user.team || "N/A"}</TableCell>
                      ) : (
                        <TableCell>{user.dsp_id || "N/A"}</TableCell>
                      )}
                      <TableCell>
                        {user.sub_teams && user.sub_teams.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.sub_teams.map((subTeam) => (
                              <Badge key={subTeam} variant="outline" className="bg-blue-50">
                                {subTeam}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            user.isActive
                              ? "bg-green-100 text-green-800"
                              : "bg-palette-jungleGreen100 text-palette-jungleGreen800"
                          }`}
                        >
                          {user.isActive ? "Active" : "Inactive"}
                        </span>
                      </TableCell>
                    </>
                  ) : isDSPDrivers ? (
                    <>
                      <TableCell>{user.fullName || "N/A"}</TableCell>
                      <TableCell>{user.person || "N/A"}</TableCell>
                      <TableCell>{user.status || "N/A"}</TableCell>
                      <TableCell>
                        {user.areas && user.areas.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.areas.map((area) => (
                              <Badge key={area} variant="outline" className="bg-blue-50">
                                {area}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>{user.dsp_id || "N/A"}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{user.fullName || "N/A"}</TableCell>
                      <TableCell>{user.person || "N/A"}</TableCell>
                      <TableCell>{user.status || "N/A"}</TableCell>
                      <TableCell>
                        {user.areas && user.areas.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {user.areas.map((area) => (
                              <Badge key={area} variant="outline" className="bg-blue-50">
                                {area}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 p-0"
                          aria-label="Open row actions"
                        >
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditUser(user)}>
                          Edit
                        </DropdownMenuItem>
                        {isStaff && user.email ? (
                          <>
                            <DropdownMenuItem
                              onClick={() => handleResendInvite(user)}
                              disabled={sendAuthLink.isPending}
                            >
                              {sendAuthLink.isPending ? "Resending..." : "Resend invite"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleSendResetPassword(user)}
                              disabled={sendResetPassword.isPending}
                            >
                              {sendResetPassword.isPending ? "Sending..." : "Send reset password"}
                            </DropdownMenuItem>
                          </>
                        ) : null}
                        <DropdownMenuItem
                          onClick={() => handleToggleActivation(user)}
                          className={user.isActive ? "text-red-600" : "text-green-600"}
                          disabled={activateUser.isPending || deactivateUser.isPending}
                        >
                          {user.isActive
                            ? deactivateUser.isPending
                              ? "Deactivating..."
                              : "Deactivate"
                            : activateUser.isPending
                              ? "Activating..."
                              : "Activate"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              // No users found
              <TableRow>
                <TableCell colSpan={columnCount} className="text-center py-4 text-muted-foreground">
                  No users found for the selected filters
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-muted-foreground">
          Page {page} of {totalPages} · Showing {users.length} of {total} users
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isLoading}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isLoading}
          >
            Next
          </Button>
        </div>
      </div>

      <CreateUserDialog
        isOpen={isCreateDialogOpen}
        setIsOpen={setIsCreateDialogOpen}
        org={org}
        kind={kind}
      />
      <EditUserDialog
        isOpen={isEditDialogOpen}
        setIsOpen={setIsEditDialogOpen}
        user={selectedUser}
      />
    </div>
  )
}

export default UserManagementContent
